import React from "react";
import { motion } from "framer-motion";

function Module3() {
  return (
    <>
      <div className="bg-particles" />
      <div className="page-container">
        <motion.div
          className="module-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="module-number">Módulo 03</p>
          <h1 className="module-title">RAG - Knowledge Bases</h1>
          <p className="module-description">
            Retrieval Augmented Generation con Amazon Bedrock Knowledge Bases.
            Dale a Nequito acceso a documentos reales de Nequi para responder con precisión.
          </p>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2>📚 ¿Qué es RAG?</h2>
          <p>
            Retrieval Augmented Generation (RAG) es un patrón que combina la capacidad generativa
            de los LLMs con la búsqueda en bases de conocimiento externas. En lugar de depender
            solo del entrenamiento del modelo, el agente busca información actualizada antes de responder.
          </p>
          <div className="architecture-diagram">
            <div className="flow">
              <div className="node">Usuario Pregunta</div>
              <div className="arrow">→</div>
              <div className="node">Búsqueda Vectorial</div>
              <div className="arrow">→</div>
              <div className="node">Documentos Relevantes</div>
              <div className="arrow">→</div>
              <div className="node">LLM + Contexto</div>
              <div className="arrow">→</div>
              <div className="node">Respuesta Precisa</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2>🔧 Paso a Paso: Crear Knowledge Base</h2>

          <div className="step-card">
            <h4>Paso 0: Crear Bucket S3</h4>
            <p>
              Crea un bucket S3 privado para almacenar los documentos de Nequi.
              Estos documentos serán la base de conocimiento del agente.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 1: Subir Documentos a S3</h4>
            <p>
              Sube los PDFs, TXTs o Markdowns con las FAQs de Nequi al bucket.
              Puede ser información sobre los productos Nequi: Cuenta Nequi, Bolsillos, NequiNegocios, etc.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 2: Crear Bedrock Knowledge Base</h4>
            <p>
              En la consola de Amazon Bedrock, crea una nueva Knowledge Base.
              Selecciona el bucket S3 como fuente de datos y vectores de búsqueda.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 3: Sincronizar los Datos</h4>
            <p>
              Ejecuta la sincronización para que Bedrock indexe los documentos,
              los divida en chunks, y genere embeddings vectoriales.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 4: Obtener IDs Necesarios</h4>
            <p>
              Anota el Knowledge Base ID y el Data Source ID. Los necesitarás
              para integrar RAG con el agente Nequito.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 5: Integrar con Nequito</h4>
            <p>
              Agrega la herramienta "faqs_nequi" al agente LangGraph que
              consulta la Knowledge Base usando la API retrieve de Bedrock.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>💻 Integración con LangGraph</h2>
          <div className="code-block">
{`# Tool RAG para Nequito
import boto3

bedrock_agent = boto3.client("bedrock-agent-runtime", region_name="us-east-1")

KNOWLEDGE_BASE_ID = "<TU_KB_ID>"

def faqs_nequi(query: str) -> str:
    """Busca información en las FAQs de Nequi."""
    response = bedrock_agent.retrieve(
        knowledgeBaseId=KNOWLEDGE_BASE_ID,
        retrievalQuery={"text": query},
        retrievalConfiguration={
            "vectorSearchConfiguration": {
                "numberOfResults": 5
            }
        },
    )

    results = []
    for item in response["retrievalResults"]:
        results.append(item["content"]["text"])

    return "\\n---\\n".join(results)`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>📄 Documentos de Ejemplo</h2>
          <p>
            El folder <code>assets/nequi-faqs/</code> contiene documentos de ejemplo
            que se suben al bucket S3 para crear la Knowledge Base:
          </p>
          <ul>
            <li><strong>productos_nequi.txt:</strong> Cuenta Nequi, Bolsillos, NequiNegocios, Tarjeta, Bre-B</li>
            <li><strong>bolsillos_ahorro.txt:</strong> Bolsillos y ahorro por metas</li>
            <li><strong>nequi_negocios.txt:</strong> NequiNegocios para emprendedores</li>
            <li><strong>tienda_nequi.txt:</strong> Catálogo y funcionamiento de la tienda</li>
            <li><strong>tarjeta_y_breb.txt:</strong> Tarjeta Nequi y pagos Bre-B</li>
          </ul>

          <div className="highlight-box">
            <h4>Ventaja del RAG para Nequi</h4>
            <p>
              Cualquier equipo puede actualizar los documentos en S3 y sincronizar la Knowledge Base.
              Nequito siempre tendrá la información más reciente sin necesidad de re-entrenar el modelo.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="source-links-banner">
            <h4>📖 Documentación Oficial AWS</h4>
            <div className="source-links-list">
              <div className="source-link-item">
                <span>Knowledge Bases →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/knowledge-base.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/knowledge-base.html
                </a>
              </div>
              <div className="source-link-item">
                <span>RAG Retrieve API →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/APIReference/API_agent-runtime_Retrieve.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/APIReference/API_agent-runtime_Retrieve.html
                </a>
              </div>
              <div className="source-link-item">
                <span>S3 User Guide →</span>
                <a href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/Welcome.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/AmazonS3/latest/userguide/Welcome.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Embeddings →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/embeddings.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/embeddings.html
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default Module3;
